import React, { useState, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  MenuItem, 
  Grid,
  Alert,
  Typography,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Chip,
  Collapse
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { collection, addDoc, getDocs, query, where, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { Save, Calendar, CreditCard, Clock, Users, FileText, Plus, Repeat, Trophy } from 'lucide-react';

function TournamentForm() {
  const { currentUser } = useAuth();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  // Updated game types
  const gameTypes = [
    { value: 'live-mtt', label: 'Live MTT (Tournament)' },
    { value: 'online-mtt', label: 'Online MTT (Tournament)' },
    { value: 'live-cash', label: 'Live Cash Game' },
    { value: 'online-cash', label: 'Online Cash Game' }
  ];
  
  // Player count ranges for tournaments
  const playerCountRanges = [
    { value: '15-30', label: '15-30 players' },
    { value: '31-50', label: '31-50 players' },
    { value: '51-80', label: '51-80 players' },
    { value: '81-100', label: '81-100 players' },
    { value: '101-150', label: '101-150 players' },
    { value: 'specify', label: 'Specify exact number' }
  ];

  const [formData, setFormData] = useState({
    name: '',
    date: dayjs(),
    buyin: '',
    rebuys: '',
    rebuyAmount: '',
    addons: '',
    addonAmount: '',
    result: '',
    gameType: 'live-mtt',
    startTime: '',
    endTime: '',
    playerRange: '15-30',
    exactPlayers: '',
    finalRanking: '',
    notes: ''
  });

  // Show rebuy/addon fields only for tournament types
  const showTournamentFields = formData.gameType === 'live-mtt' || formData.gameType === 'online-mtt';
  
  // Show time fields only for cash games
  const showTimeFields = formData.gameType === 'live-cash' || formData.gameType === 'online-cash';
  
  // Show player count only for tournaments
  const showPlayerCount = formData.gameType === 'live-mtt' || formData.gameType === 'online-mtt';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (newDate) => {
    setFormData(prev => ({
      ...prev,
      date: newDate
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    setSuccess(false);
    
    try {
      // Enhanced form validation
      const requiredFields = [
        { field: 'name', label: 'Tournament/Game Name' },
        { field: 'buyin', label: 'Buy-in Amount' },
        { field: 'result', label: 'Result' }
      ];
      
      // Add conditional required fields based on game type
      if (showTournamentFields) {
        requiredFields.push(
          { field: 'rebuys', label: 'Number of Rebuys' },
          { field: 'rebuyAmount', label: 'Rebuy Amount' },
          { field: 'addons', label: 'Number of Add-ons' },
          { field: 'addonAmount', label: 'Add-on Amount' }
        );
      }
      
      if (showTimeFields) {
        requiredFields.push(
          { field: 'startTime', label: 'Start Time' },
          { field: 'endTime', label: 'End Time' }
        );
      }
      
      // Check for empty required fields
      const emptyFields = requiredFields.filter(field => 
        formData[field.field] === '' || formData[field.field] === null || formData[field.field] === undefined
      );
      
      if (emptyFields.length > 0) {
        throw new Error(`Please fill in the following required fields: ${emptyFields.map(f => f.label).join(', ')}`);
      }
      
      // Calculate total investment
      let totalInvestment = parseFloat(formData.buyin);
      
      if (showTournamentFields) {
        // Add rebuys if provided
        if (formData.rebuys && formData.rebuyAmount) {
          totalInvestment += parseInt(formData.rebuys) * parseFloat(formData.rebuyAmount);
        }
        
        // Add addons if provided
        if (formData.addons && formData.addonAmount) {
          totalInvestment += parseInt(formData.addons) * parseFloat(formData.addonAmount);
        }
      }
      
      // Determine player count
      let playerCount = null;
      if (showPlayerCount) {
        if (formData.playerRange === 'specify' && formData.exactPlayers) {
          playerCount = parseInt(formData.exactPlayers);
        } else {
          playerCount = formData.playerRange;
        }
      }
      
      // Format the date properly for Firebase
      let formattedDate;
      if (formData.date && typeof formData.date.format === 'function') {
        // If using dayjs object
        formattedDate = formData.date.format('YYYY-MM-DD');
      } else if (formData.date instanceof Date) {
        // If using Date object
        formattedDate = formData.date.toISOString().split('T')[0];
      } else {
        // Use as is if it's already a string
        formattedDate = formData.date;
      }
      
      // Create the tournament data object
      const tournamentData = {
        ...formData,
        userId: currentUser.uid,
        buyin: Number(formData.buyin),
        result: Number(formData.result),
        date: formattedDate,
        // Ensure player count is saved with the correct field name
        players: formData.playerRange || formData.exactPlayers || 'N/A',
        // Calculate profit
        profit: parseFloat(formData.result) - totalInvestment,
        totalInvestment: totalInvestment,
        gameType: formData.gameType,
        startTime: showTimeFields ? formData.startTime : null,
        endTime: showTimeFields ? formData.endTime : null,
        playerCount: playerCount,
        notes: formData.notes,
        createdAt: new Date()
      };
      
      await addDoc(collection(db, "tournaments"), tournamentData);
      
      // Reset form
      setFormData({
        name: '',
        date: dayjs(),
        buyin: '',
        rebuys: '',
        rebuyAmount: '',
        addons: '',
        addonAmount: '',
        result: '',
        gameType: 'live-mtt',
        startTime: '',
        endTime: '',
        playerRange: '15-30',
        exactPlayers: '',
        finalRanking: '',
        notes: ''
      });
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Session added successfully!
        </Alert>
      )}
      
      <Box component="form" onSubmit={handleSubmit} sx={{ pl: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="game-type-label">Game Type</InputLabel>
              <Select
                labelId="game-type-label"
                name="gameType"
                value={formData.gameType}
                onChange={handleChange}
                label="Game Type"
              >
                {gameTypes.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Tournament/Game Name *"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              error={formData.name === '' && error !== null}
              helperText={formData.name === '' && error !== null ? 'Tournament/Game Name is required' : ''}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FileText size={20} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Date"
                value={formData.date}
                onChange={handleDateChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    InputProps: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Calendar size={20} />
                        </InputAdornment>
                      ),
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          
          {showPlayerCount && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="player-range-label">Number of Players</InputLabel>
                <Select
                  labelId="player-range-label"
                  name="playerRange"
                  value={formData.playerRange}
                  onChange={handleChange}
                  label="Number of Players"
                  startAdornment={
                    <InputAdornment position="start">
                      <Users size={20} />
                    </InputAdornment>
                  }
                >
                  {playerCountRanges.map(range => (
                    <MenuItem key={range.value} value={range.value}>
                      {range.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Buy-in Amount"
              name="buyin"
              type="number"
              value={formData.buyin}
              onChange={handleChange}
              required
              error={formData.buyin === '' && error !== null}
              helperText={formData.buyin === '' && error !== null ? 'Buy-in Amount is required' : ''}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    R
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Winnings"
              name="result"
              type="number"
              value={formData.result}
              onChange={handleChange}
              required
              error={formData.result === '' && error !== null}
              helperText={formData.result === '' && error !== null ? 'Winnings is required' : ''}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    R
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          {showTournamentFields && (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Rebuy Amount"
                  name="rebuyAmount"
                  type="number"
                  value={formData.rebuyAmount}
                  onChange={handleChange}
                  required
                  error={formData.rebuyAmount === '' && error !== null}
                  helperText={formData.rebuyAmount === '' && error !== null ? 'Rebuy Amount is required' : ''}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        R
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Number of Rebuys"
                  name="rebuys"
                  type="number"
                  value={formData.rebuys}
                  onChange={handleChange}
                  required
                  error={formData.rebuys === '' && error !== null}
                  helperText={formData.rebuys === '' && error !== null ? 'Number of Rebuys is required' : ''}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Repeat size={20} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Add-on Amount"
                  name="addonAmount"
                  type="number"
                  value={formData.addonAmount}
                  onChange={handleChange}
                  required
                  error={formData.addonAmount === '' && error !== null}
                  helperText={formData.addonAmount === '' && error !== null ? 'Add-on Amount is required' : ''}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        R
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Number of Add-ons"
                  name="addons"
                  type="number"
                  value={formData.addons}
                  onChange={handleChange}
                  required
                  error={formData.addons === '' && error !== null}
                  helperText={formData.addons === '' && error !== null ? 'Number of Add-ons is required' : ''}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Plus size={20} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Final Ranking (optional)"
                  name="finalRanking"
                  type="number"
                  value={formData.finalRanking}
                  onChange={handleChange}
                  placeholder="e.g., 3 for 3rd place"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Trophy size={20} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </>
          )}
          
          {formData.playerRange === 'specify' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Exact Player Count"
                name="exactPlayers"
                type="number"
                value={formData.exactPlayers}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Users size={20} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          )}
          
          {showTimeFields && (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Start Time"
                  name="startTime"
                  type="time"
                  value={formData.startTime}
                  onChange={handleChange}
                  required
                  error={formData.startTime === '' && error !== null}
                  helperText={formData.startTime === '' && error !== null ? 'Start Time is required' : ''}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Clock size={20} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="End Time"
                  name="endTime"
                  type="time"
                  value={formData.endTime}
                  onChange={handleChange}
                  required
                  error={formData.endTime === '' && error !== null}
                  helperText={formData.endTime === '' && error !== null ? 'End Time is required' : ''}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Clock size={20} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </>
          )}
          
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }}>
              <Chip label="Additional Details" />
            </Divider>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary" 
              fullWidth
              disabled={loading}
              startIcon={<Save />}
              size="large"
              sx={{ py: 1.5 }}
            >
              {loading ? 'Adding Session...' : 'Save Poker Session'}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
}

export default TournamentForm; 